import React from "react";
import Card from "../card";

interface Props {
  icon: React.ReactNode;
  title: string;
  /** Ações da tela (ex.: "Abrir chamado"), à direita do título. */
  actions?: React.ReactNode;
  /** Filtros da tela. Ficam NO cabeçalho e valem para todos os blocos abaixo dele. */
  filters?: React.ReactNode;
  /** Faixa de indicadores logo abaixo dos filtros (ver `KpiStrip`). */
  kpis?: React.ReactNode;
  /** Conteúdo extra recolhível sob os indicadores (detalhes do indicador). */
  children?: React.ReactNode;
}

/**
 * Cabeçalho padrão de tela: título, filtros e indicadores num bloco só e baixo.
 *
 * A regra que ele carrega: a tela tem UMA barra de filtros, e todo bloco abaixo (indicadores,
 * gráfico, lista) responde a ela. Filtro dentro de um bloco só filtra aquele bloco, e a tela passa
 * a mostrar números que não conversam entre si. O conteúdo principal (a lista) vem logo depois
 * deste cabeçalho; detalhe de indicador entra recolhido em `children`.
 */
const PageHeader: React.FC<Props> = ({ icon, title, actions, filters, kpis, children }) => (
  <Card extra="p-4 sm:px-5">
    <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
      <div className="flex items-center justify-between gap-3 xl:mb-1 xl:justify-start">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-dv-gradient text-white shadow-dv-glow"
            aria-hidden="true"
          >
            {icon}
          </span>
          <h1 className="whitespace-nowrap text-xl font-extrabold tracking-tight text-navy-700 dark:text-white">{title}</h1>
        </div>
        {actions && <div className="flex items-center gap-2 xl:hidden">{actions}</div>}
      </div>

      {filters && (
        <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:ml-4">{filters}</div>
      )}

      {actions && <div className="hidden shrink-0 items-center gap-2 xl:mb-0.5 xl:flex">{actions}</div>}
    </div>

    {kpis && <div className="mt-3">{kpis}</div>}
    {children}
  </Card>
);

/** Rótulo + controle de um filtro do cabeçalho, no tamanho compacto. */
export const FilterField: React.FC<{ label: string; htmlFor?: string; children: React.ReactNode }> = ({
  label,
  htmlFor,
  children,
}) => (
  <div className="flex min-w-0 flex-col gap-1">
    {htmlFor ? (
      <label htmlFor={htmlFor} className="text-xs font-semibold text-gray-700 dark:text-gray-300">
        {label}
      </label>
    ) : (
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</span>
    )}
    {children}
  </div>
);

export default PageHeader;
